import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { Marketplace, ProductTrackerConfig } from "@front/m19-api-client";
import { Catalog, SegmentConfigType, SegmentEx } from "@front/m19-models";
import { SegmentService } from "@front/m19-services";
import { Utils } from "@front/m19-utils";
import { TranslocoDirective } from "@jsverse/transloco";
import { ProductViewComponent } from "@m19-board/product-view/product-view.component";
import {
  IButtonDirective,
  IInputGroupDirective,
  IInputGroupInput,
  ISwitchButtonComponent,
  ITooltipDirective,
  MODAL_DATA,
  ModalRef,
  IIconComponent,
} from "@front/m19-ui";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";

enum ProductSelectionMode {
  Bulk,
  FromProductSegment,
}

@UntilDestroy()
@Component({
  templateUrl: "./add-product-tracking-modal.component.html",
  styleUrls: ["./add-product-tracking-modal.component.scss"],
  imports: [
    TranslocoDirective,
    FormsModule,
    ProductViewComponent,
    IButtonDirective,
    ITooltipDirective,
    IInputGroupDirective,
    IInputGroupInput,
    ISwitchButtonComponent,
    IIconComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddProductTrackingModalComponent implements OnInit {
  private segmentService = inject(SegmentService);

  readonly SelectionModes = ProductSelectionMode;

  protected readonly data = inject(MODAL_DATA) as AddProductTrackingModalData;
  modalRef = inject(ModalRef);

  bulkAsins = "";
  selectionMode = ProductSelectionMode.Bulk;
  asinList: string[] = [];
  productSegments = signal<SegmentEx[] | undefined>(undefined);
  selectedProductSegments = signal<SegmentEx[]>([]);
  filteredProductSegments = signal<SegmentEx[]>([]);
  productSegmentItems = new Map<number, string[]>();
  productSegmentFilter = new RegExp("", "i");
  errors?: string[];
  productFilter = new RegExp("", "i");

  constructor() {
    this.asinList = this.data.products;
  }

  get filteredAsinList(): string[] {
    return this.asinList.filter((asin) => this.productFilter.test(asin));
  }

  ngOnInit(): void {
    this.segmentService
      .getSegments(this.data.accountId, this.data.marketplace)
      .pipe(untilDestroyed(this))
      .subscribe((segmentsMap: Map<number, SegmentEx>) => {
        const productSegments = Array.from(segmentsMap.values()).filter(
          (seg) => seg.segmentType === SegmentConfigType.ProductSegment,
        );
        this.productSegmentFilter = new RegExp("", "i");
        for (const segment of productSegments) {
          this.productSegmentItems.set(
            segment.segmentId,
            segment.items
              .map((item) => item.targetingValue)
              .filter(
                (asin) =>
                  !this.data.catalog.contains(asin) && !this.data.productTrackerConfig.find((c) => c.asin === asin),
              ),
          );
        }
        this.productSegments.set(productSegments);
        this.selectedProductSegments.set([]);
        this.filteredProductSegments.set(productSegments);
      });
  }

  addProduct(): void {
    if (this.asinToAddInvalid()) {
      return;
    }
    this.errors = [];
    const bulk = this.bulkAsins
      .split("\n")
      .map((x) => x.trim())
      .filter((x) => x !== "");
    const asinsToAdd: string[] = [];
    for (const asinToAdd of bulk) {
      if (!Utils.isValidAsin(asinToAdd)) {
        this.errors.push(`${asinToAdd} is not a valid ASIN`);
        continue;
      }
      if (this.data.catalog.contains(asinToAdd)) {
        this.errors.push(`ASIN ${asinToAdd} is in your catalog (it is already tracked)`);
        continue;
      }
      if (
        this.data.productTrackerConfig.findIndex((c) => c.asin == asinToAdd) >= 0 ||
        this.asinList.includes(asinToAdd)
      ) {
        this.errors.push(`ASIN ${asinToAdd} is already tracked`);
        continue;
      }
      if (asinsToAdd.includes(asinToAdd)) {
        this.errors.push(`ASIN ${asinToAdd} is duplicated`);
        continue;
      }
      asinsToAdd.push(asinToAdd);
    }
    this.asinList.push(...asinsToAdd);
    this.bulkAsins = "";
  }

  asinToAddInvalid(): boolean {
    return this.bulkAsins === "";
  }

  deleteAsin(asin: string) {
    this.asinList.splice(this.asinList.indexOf(asin), 1);
  }

  deleteAllAsins() {
    this.asinList = [];
  }

  canSave() {
    return this.asinList.length > 0;
  }

  onSave(): void {
    const terms: ProductTrackerConfig[] = [];
    this.asinList.forEach((asin) => {
      terms.push({
        accountId: this.data.accountId,
        marketplace: this.data.marketplace,
        asin: asin,
      });
    });
    this.modalRef.close(terms);
  }

  changeProductSelectionMode(mode: ProductSelectionMode) {
    this.selectedProductSegments.set([]);
    this.bulkAsins = "";
    this.selectionMode = mode;
    this.errors = [];
  }

  isProductSegmentAddButtonDisabled() {
    return this.selectedProductSegments().length === 0;
  }

  selectAllProductSegments() {
    const selected = [...this.selectedProductSegments()];
    for (const segment of this.filteredProductSegments()) {
      if (!selected.some((seg) => seg.segmentId == segment.segmentId)) {
        selected.push(segment);
      }
    }
    this.selectedProductSegments.set(selected);
  }

  unselectAllProductSegments() {
    this.selectedProductSegments.set([]);
  }

  setProductSegmentFilter(value: string) {
    this.productSegmentFilter = new RegExp(value, "i");
    this.filteredProductSegments.set(
      this.productSegments()?.filter((seg) => this.productSegmentFilter.test(seg.name)) ?? [],
    );
  }

  selectProductSegment(productSegment: SegmentEx) {
    const selected = this.selectedProductSegments();
    const index = selected.findIndex((seg) => seg.segmentId === productSegment.segmentId);
    if (index < 0) {
      this.selectedProductSegments.set([...selected, productSegment]);
    } else {
      this.selectedProductSegments.set(selected.filter((_, i) => i !== index));
    }
  }

  getProductSegmentItems(productSegment: SegmentEx) {
    return this.productSegmentItems.get(productSegment.segmentId) ?? [];
  }

  isProductSegmentSelected(productSegment: SegmentEx) {
    return this.selectedProductSegments().findIndex((seg) => seg.segmentId === productSegment.segmentId) >= 0;
  }

  productSegmentAlreadySelected(productSegment: SegmentEx) {
    return this.getProductSegmentItems(productSegment).every((kw) => this.asinList.includes(kw));
  }

  addProductsByProductSegment() {
    for (const productSegment of this.selectedProductSegments()) {
      for (const asin of this.getProductSegmentItems(productSegment)) {
        if (this.asinList.findIndex((x) => x === asin) < 0) this.asinList.push(asin);
      }
    }
  }

  setProductFilter(value: string) {
    this.productFilter = new RegExp(value, "i");
  }
}

export interface AddProductTrackingModalData {
  accountId: string;
  marketplace: Marketplace;
  catalog: Catalog;
  productTrackerConfig: ProductTrackerConfig[];
  products: string[];
}
